// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

// Supported-locale registry. Adding a locale is two steps:
//   1. Drop `src/i18n/locales/<code>.json` (auto-loaded by config.ts via glob).
//   2. Add one entry here with its native name and script (for the font stack).
// See src/i18n/locales/README.md.

// `script` drives the per-locale font stack (see index.css / config.ts). Latin
// locales render in IBM Plex Mono/Inter unchanged; the four CJK scripts switch
// to the matching IBM Plex Sans CJK family because Plex Mono has no CJK glyphs;
// `hebrew`/`arabic` fall through to the system Hebrew/Arabic families (Inter
// has no glyphs for either). Arabic covers both ar and fa (Perso-Arabic).
export type LocaleScript = 'latin' | 'cjk-sc' | 'cjk-tc' | 'cjk-jp' | 'cjk-kr' | 'hebrew' | 'arabic';

export interface LocaleMeta {
  /** BCP-47 code, also the resource key and the locale passed to Intl.*. */
  code: string;
  /** Endonym shown in the language switcher (always in its own language). */
  nativeName: string;
  /** English name, for docs / accessibility. */
  englishName: string;
  script: LocaleScript;
  dir: 'ltr' | 'rtl';
}

export const LOCALES: LocaleMeta[] = [
  { code: 'en', nativeName: 'English', englishName: 'English', script: 'latin', dir: 'ltr' },
  { code: 'zh-Hans', nativeName: '简体中文', englishName: 'Chinese (Simplified)', script: 'cjk-sc', dir: 'ltr' },
  { code: 'zh-Hant', nativeName: '繁體中文', englishName: 'Chinese (Traditional)', script: 'cjk-tc', dir: 'ltr' },
  { code: 'ja', nativeName: '日本語', englishName: 'Japanese', script: 'cjk-jp', dir: 'ltr' },
  { code: 'ko', nativeName: '한국어', englishName: 'Korean', script: 'cjk-kr', dir: 'ltr' },
  { code: 'es', nativeName: 'Español', englishName: 'Spanish', script: 'latin', dir: 'ltr' },
  { code: 'de', nativeName: 'Deutsch', englishName: 'German', script: 'latin', dir: 'ltr' },
  { code: 'sv', nativeName: 'Svenska', englishName: 'Swedish', script: 'latin', dir: 'ltr' },
  { code: 'da', nativeName: 'Dansk', englishName: 'Danish', script: 'latin', dir: 'ltr' },
  { code: 'nb', nativeName: 'Norsk bokmål', englishName: 'Norwegian Bokmål', script: 'latin', dir: 'ltr' },
  { code: 'fr', nativeName: 'Français', englishName: 'French', script: 'latin', dir: 'ltr' },
  { code: 'it', nativeName: 'Italiano', englishName: 'Italian', script: 'latin', dir: 'ltr' },
  { code: 'pt-BR', nativeName: 'Português (Brasil)', englishName: 'Portuguese (Brazil)', script: 'latin', dir: 'ltr' },
  { code: 'pt-PT', nativeName: 'Português (Portugal)', englishName: 'Portuguese (Portugal)', script: 'latin', dir: 'ltr' },
  { code: 'fi', nativeName: 'Suomi', englishName: 'Finnish', script: 'latin', dir: 'ltr' },
  { code: 'nl', nativeName: 'Nederlands', englishName: 'Dutch', script: 'latin', dir: 'ltr' },
  { code: 'pl', nativeName: 'Polski', englishName: 'Polish', script: 'latin', dir: 'ltr' },
  { code: 'cs', nativeName: 'Čeština', englishName: 'Czech', script: 'latin', dir: 'ltr' },
  { code: 'tr', nativeName: 'Türkçe', englishName: 'Turkish', script: 'latin', dir: 'ltr' },
  { code: 'ru', nativeName: 'Русский', englishName: 'Russian', script: 'latin', dir: 'ltr' },
  { code: 'uk', nativeName: 'Українська', englishName: 'Ukrainian', script: 'latin', dir: 'ltr' },
  { code: 'he', nativeName: 'עברית', englishName: 'Hebrew', script: 'hebrew', dir: 'rtl' },
  { code: 'ar', nativeName: 'العربية', englishName: 'Arabic', script: 'arabic', dir: 'rtl' },
  { code: 'fa', nativeName: 'فارسی', englishName: 'Persian', script: 'arabic', dir: 'rtl' },
];

export const DEFAULT_LOCALE = 'en';

export const SUPPORTED_LOCALE_CODES: string[] = LOCALES.map((l) => l.code);

export function getLocaleMeta(code: string): LocaleMeta | undefined {
  return LOCALES.find((l) => l.code === code);
}

/** The script class for a locale code, defaulting to Latin for unknowns. */
export function getLocaleScript(code: string): LocaleScript {
  return getLocaleMeta(code)?.script ?? 'latin';
}
